import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Shield, Store, Users, ShoppingBag, Coins, TrendingUp, LogOut, Phone, Copy, ExternalLink, Search, ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { money, STATUS_META, DELIVERY_COLOR, COUNTRIES } from "@/lib/orderStatus";
import { useAuth } from "@/context/AuthContext";
import { AdminVideoCredits } from "@/components/AdminVideoCredits";

export default function Admin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [commPeriod, setCommPeriod] = useState("all");
  const [bizQuery, setBizQuery] = useState("");
  const [imgMig, setImgMig] = useState(null);
  const pollRef = useRef(null);
  const { data: overview } = useQuery({ queryKey: ["admin-overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const { data: businesses = [] } = useQuery({ queryKey: ["admin-businesses"], queryFn: async () => (await api.get("/admin/businesses")).data });
  const { data: orders = [] } = useQuery({ queryKey: ["admin-orders"], queryFn: async () => (await api.get("/admin/orders")).data });
  const { data: commissions } = useQuery({ queryKey: ["admin-commissions", commPeriod], queryFn: async () => (await api.get(`/admin/commissions?period=${commPeriod}`)).data });

  const fetchMigStatus = async () => {
    try {
      const { data } = await api.get("/admin/optimize-images/status");
      setImgMig(data);
      if (!data.running && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    } catch (e) { /* noop */ }
  };
  const startMig = async () => {
    try {
      const { data } = await api.post("/admin/optimize-images");
      if (data.started === false && !data.already_running) return;
      toast.success("Optimisation lancée");
      if (!pollRef.current) pollRef.current = setInterval(fetchMigStatus, 2000);
      fetchMigStatus();
    } catch (e) { toast.error("Impossible de lancer l'optimisation"); }
  };

  useEffect(() => {
    if (user && user.role === "admin") fetchMigStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (imgMig?.running && !pollRef.current) pollRef.current = setInterval(fetchMigStatus, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgMig?.running]);

  useEffect(() => {
    if (user && user.role !== "admin") navigate("/tableau-de-bord", { replace: true });
  }, [user, navigate]);

  if (user && user.role !== "admin") return null;

  const q = bizQuery.trim().toLowerCase();
  const filteredBusinesses = q
    ? businesses.filter((b) => `${b.name || ""} ${b.slug || ""}`.toLowerCase().includes(q))
    : businesses;

  return (
    <div className="min-h-screen bg-[#0F1B16] text-white">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="h-10 w-10 rounded-xl bg-[#E85D04] flex items-center justify-center"><Shield className="h-5 w-5" /></span>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#FFBA08]">Administration</p>
              <h1 className="font-display text-xl font-extrabold">SuccèsNet — Supervision</h1>
            </div>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout} className="h-10 px-4 rounded-xl bg-white/10 font-semibold text-sm flex items-center gap-1.5"><LogOut className="h-4 w-4" /> Quitter</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
          <AdminKpi icon={Store} label="Boutiques" value={overview?.businesses ?? 0} color="#E85D04" testid="admin-kpi-businesses" />
          <AdminKpi icon={Users} label="Entrepreneurs" value={overview?.users ?? 0} color="#0077B6" testid="admin-kpi-entrepreneurs" />
          <AdminKpi icon={ShoppingBag} label="Commandes" value={overview?.total_orders ?? 0} color="#FFBA08" testid="admin-kpi-orders" />
          <AdminKpi icon={TrendingUp} label="GMV (payé)" value={money(overview?.gmv, "XOF")} color="#2D6A4F" testid="admin-kpi-gmv" />
          <AdminKpi icon={Coins} label="Commissions (2,5%)" value={money(overview?.commission, "XOF")} color="#E85D04" testid="admin-kpi-commissions" />
          <AdminKpi icon={ShoppingBag} label="Commandes payées" value={overview?.paid_orders ?? 0} color="#0077B6" testid="admin-kpi-paid" />
        </div>

        <div className="bg-white/5 rounded-2xl p-4 mb-6" data-testid="admin-image-optimizer">
          <div className="flex items-center gap-2 mb-1">
            <ImageIcon className="h-5 w-5 text-[#FFBA08]" />
            <h2 className="font-display text-lg font-bold">Optimisation des images</h2>
          </div>
          <p className="text-xs text-white/50 mb-3">Compresse les images produits déjà en ligne pour accélérer l'affichage des vitrines. Sans perte visible. Peut être relancé sans risque.</p>
          <div className="flex flex-wrap items-center gap-3">
            <button data-testid="optimize-images-btn" onClick={startMig} disabled={imgMig?.running}
              className="h-11 px-5 rounded-xl bg-[#E85D04] font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
              {imgMig?.running ? <><Loader2 className="h-4 w-4 animate-spin" /> Optimisation en cours…</> : "Optimiser les images"}
            </button>
            {imgMig && (imgMig.running || imgMig.done > 0) && (
              <div className="text-sm text-white/70" data-testid="optimize-images-status">
                {imgMig.total > 0 && <span>{imgMig.done}/{imgMig.total} traitées · </span>}
                <span>{imgMig.shrunk || 0} compressées</span>
                {imgMig.saved_bytes > 0 && <span> · {(imgMig.saved_bytes / 1048576).toFixed(1)} Mo économisés</span>}
                {!imgMig.running && imgMig.finished_at && <span className="text-[#2ecc71]"> ✓ terminé</span>}
              </div>
            )}
          </div>
        </div>

        <AdminVideoCredits />

        <h2 className="font-display text-lg font-bold mb-3">Boutiques</h2>
        <div className="relative mb-3">
          <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={bizQuery}
            onChange={(e) => setBizQuery(e.target.value)}
            placeholder="Rechercher une boutique (nom ou lien)…"
            data-testid="admin-business-search"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#E85D04]"
          />
        </div>
        <div className="bg-white/5 rounded-2xl overflow-hidden mb-8" data-testid="admin-businesses-table">
          <div className="grid grid-cols-12 px-4 py-2 text-xs text-white/50 font-semibold border-b border-white/10">
            <span className="col-span-5">Boutique</span><span className="col-span-2">Pays</span><span className="col-span-2 text-right">Cmd</span><span className="col-span-3 text-right">Commission</span>
          </div>
          {filteredBusinesses.map((b) => (
            <div key={b.business_id} className="grid grid-cols-12 px-4 py-3 text-sm border-b border-white/5 items-center">
              <div className="col-span-5 min-w-0">
                <p className="font-medium truncate">{b.name}</p>
                {b.slug && (
                  <div className="flex items-center gap-2 mt-0.5">
                    <a
                      href={`/boutique/${b.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-store-link-${b.slug}`}
                      className="text-xs text-[#48CAE4] hover:underline truncate inline-flex items-center gap-1"
                      title="Ouvrir la boutique"
                    >
                      /boutique/{b.slug} <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                    <button
                      type="button"
                      data-testid={`admin-copy-link-${b.slug}`}
                      onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/boutique/${b.slug}`); toast.success("Lien copié"); }}
                      className="text-white/40 hover:text-white shrink-0"
                      title="Copier le lien"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <span className="col-span-2">{COUNTRIES[b.country]?.flag || ""} {b.country}</span>
              <span className="col-span-2 text-right text-white/70">{b.order_count}</span>
              <span className="col-span-3 text-right font-semibold text-[#FFBA08]">{money(b.commission, b.currency)}</span>
            </div>
          ))}
          {filteredBusinesses.length === 0 && <p className="p-4 text-white/40 text-sm" data-testid="admin-no-business">{businesses.length === 0 ? "Aucune boutique." : "Aucune boutique ne correspond à votre recherche."}</p>}
        </div>

        <h2 className="font-display text-lg font-bold mb-3">Commissions à facturer</h2>
        <p className="text-white/40 text-xs mb-3">Commission 2,5 % à encaisser manuellement auprès du marchand (paiements Mobile Money / espèces / liens manuels). La part Stripe (carte CA/FR) est déjà prélevée automatiquement.</p>
        <div className="flex gap-2 mb-3" data-testid="commission-period-toggle">
          {[["all", "Depuis le début"], ["month", "Ce mois-ci"]].map(([k, lbl]) => (
            <button key={k} data-testid={`commission-period-${k}`} onClick={() => setCommPeriod(k)}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${commPeriod === k ? "bg-[#E85D04] text-white" : "bg-white/10 text-white/60"}`}>{lbl}</button>
          ))}
        </div>

        {commissions?.totals_to_invoice && Object.keys(commissions.totals_to_invoice).length > 0 && (
          <div className="flex flex-wrap gap-3 mb-3" data-testid="commission-totals">
            {Object.entries(commissions.totals_to_invoice).map(([cur, amt]) => (
              <div key={cur} className="bg-[#E85D04]/15 border border-[#E85D04]/30 rounded-xl px-4 py-2">
                <p className="text-[10px] text-white/50 font-medium">Total à facturer ({cur})</p>
                <p className="font-display font-bold text-[#FFBA08]">{money(amt, cur)}</p>
              </div>
            ))}
          </div>
        )}

        <div className="bg-white/5 rounded-2xl overflow-hidden mb-8" data-testid="admin-commissions-table">
          <div className="grid grid-cols-12 px-4 py-2 text-xs text-white/50 font-semibold border-b border-white/10">
            <span className="col-span-4">Boutique</span><span className="col-span-2">Contact</span><span className="col-span-2 text-right">Cmd</span><span className="col-span-2 text-right">À facturer</span><span className="col-span-2 text-right">Stripe (auto)</span>
          </div>
          {(commissions?.businesses || []).map((b) => (
            <div key={b.business_id} className="grid grid-cols-12 px-4 py-3 text-sm border-b border-white/5 items-center" data-testid={`commission-row-${b.business_id}`}>
              <span className="col-span-4 font-medium truncate">{COUNTRIES[b.country]?.flag || ""} {b.name}</span>
              <span className="col-span-2 truncate">
                {b.whatsapp ? (
                  <a href={`https://wa.me/${b.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-[#25D366] flex items-center gap-1 text-xs"><Phone className="h-3 w-3" /> WhatsApp</a>
                ) : <span className="text-white/30 text-xs">—</span>}
              </span>
              <span className="col-span-2 text-right text-white/70">{b.orders_to_invoice}</span>
              <span className="col-span-2 text-right font-bold text-[#FFBA08]" data-testid={`commission-toinvoice-${b.business_id}`}>{money(b.to_invoice, b.currency)}</span>
              <span className="col-span-2 text-right text-white/40 text-xs">{money(b.stripe_collected, b.currency)}</span>
            </div>
          ))}
          {(!commissions?.businesses || commissions.businesses.length === 0) && <p className="p-4 text-white/40 text-sm" data-testid="commission-empty">Aucune commission à facturer sur cette période.</p>}
        </div>

        <h2 className="font-display text-lg font-bold mb-3">Commandes récentes</h2>
        <div className="space-y-2" data-testid="admin-orders-list">
          {orders.slice(0, 30).map((o) => {
            const meta = STATUS_META[o.status];
            const dc = DELIVERY_COLOR[o.delivery_color] || DELIVERY_COLOR.green;
            return (
              <div key={o.order_id} className="bg-white/5 rounded-xl px-4 py-3 flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span className={`h-2.5 w-2.5 rounded-full ${dc.dot}`} />
                  {o.customer_name} <span className="text-white/40">#{o.order_id.slice(-6)}</span>
                </span>
                <span className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${meta.cls}`}>{meta.label}</span>
                  <span className="font-semibold">{money(o.total, o.currency)}</span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AdminKpi({ icon: Icon, label, value, color, testid }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white/5 rounded-2xl p-4" data-testid={testid}>
      <span className="h-9 w-9 rounded-lg flex items-center justify-center mb-2" style={{ background: `${color}33` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </span>
      <p className="text-xs text-white/50 font-medium">{label}</p>
      <p className="font-display font-bold text-lg mt-0.5">{value}</p>
    </motion.div>
  );
}
